import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
/**
 * Project : HSS-websire
 * @class Info Api Service
 * @author Waruna Gamage
 */
export class InfoApiService {

  private faculty_id: number;

  constructor(private apiService: ApiService) {
    this.faculty_id = environment.faculty_id;
  }

  /**
   * @method Site search
   * @params infoId
   */
  siteSearch(phase: string): Observable<any> {
    return this.apiService.get('/info/search/' + this.faculty_id + '/' + phase);
  }

  /**
   * @method Get Active information for faculty
   * @params infoId
   */
  getActiveInfo(infoId: number): Observable<any> {
    return this.apiService.get('/info/byfaculty/' + this.faculty_id + '/' + infoId);
  }

  /**
   * @method Get all Archive information for faculty
   * @params infoId
   */
  getActiveArchiveInfo(infoId: number): Observable<any> {
    return this.apiService.get('/info/byfaculty/archive/' + this.faculty_id + '/' + infoId);
  }

  /**
   * @method Get Active information by department
   * @params deptId
   */
  getActiveInfoByDept(deptId: number, infoId: number): Observable<any> {
    return this.apiService.get('/info/bydept/' + deptId + '/' + infoId);
  }

  /**
   * @method Get Archive information by department
   * @params deptId
   */
  getActiveArchiveInfoByDept(deptId: number, infoId: number): Observable<any> {
    return this.apiService.get('/info/bydept/archive/' + deptId + '/' + infoId);
  }


  /**
   * @method Get info by id
   */
  getInformation(id: number): Observable<any> {
    return this.apiService.get('/info/' + id);
  }

  /**
   * @method Get slides for given slider id
   */
  getSlides(sliderid: number): Observable<any> {
    return this.apiService.get('/slide/bysliderid/' + sliderid);
  }

  /**
   * @method Get slides for given slider name
   */
  getSlidesByName(sliderName: string): Observable<any> {
    return this.apiService.get('/slide/slider/byname/' + sliderName);
  }

  /**
   * @method Get Gallery Images for given information id
   */
  getGalleyImages(infoId: number): Observable<any> {
    return this.apiService.get('/info_vs_slider/infoId/' + infoId);
  }

  /**
   * @method Get All Departments
   */
  getDepartments(): Observable<any> {
    return this.apiService.get('/department/viewall');
  }

  /**
   * @method Get All Departments list by dept type
   */
  getDeptByType(): Observable<any> {
    return this.apiService.get('/department/bytype/byfaculty/' + this.faculty_id);
  }

  /**
   * @method Get Departments list for menus
   */
  getDeptListMenu(): Observable<any> {
    return this.apiService.get('/department/listmenu/byfaculty/' + this.faculty_id);
  }

  /**
   * @method Get Department by id
   */
  getDepartmentById(id: number): Observable<any> {
    return this.apiService.get('/department/view/' + id);
  }

  /**
   * @method Get Counts of staff, degree, faq by department id
   */
  getCountsByDeptId(id: number): Observable<any> {
    return this.apiService.get('/department/count/' + id);
  }

  /**
   * @method Get Degrees by Department id
   */
  getDegreesByDept(id: number): Observable<any> {
    return this.apiService.get('/degree/view/deptid/' + id);
  }

  /**
   * @method Get Degree courses by Degree id
   */
  getCourseByDegId(id: number): Observable<any> {
    return this.apiService.get('/degree_vs_course/view/degreeid/' + id);
  }

  /**
   * @method Get Faculty by id
   */
  getFacultyById(id: number): Observable<any> {
    return this.apiService.get('/faculty/view/' + id);
  }

  /**
   * @method Get All FAQ for a Department
   */
  getAllFaqByDept(id: number): Observable<any> {
    return this.apiService.get('/faq_vs_dept/view/deptid/' + id);
  }

  /**
   * @method Get All FAQ for a Faculty
   */
  getAllFaqByFaculty(): Observable<any> {
    return this.apiService.get('/faq_vs_faculty/view/facultyid/' + this.faculty_id);
  }

  /**
   * @method Get All Staff categories
   */
  getStaffCategories(): Observable<any> {
    return this.apiService.get('/staff_category/view');
  }

  /**
   * @method Get Staff by Category id
   */
  getStaffByCat(id: number): Observable<any> {
    return this.apiService.get('/staff/bycatid/byfaculty/' + id + '/' + this.faculty_id);
  }

  /**
   * @method Get Staff member
   */
  getStaffMember(id: number): Observable<any> {
    return this.apiService.get('/staff/byid/' + id);
  }

  /**
   * @method Get Staff member
   */
  getMemberByProfile(profile: string): Observable<any> {
    return this.apiService.get('/staff/profile/' + profile);
  }

  /**
   * @method Get links by staff id
   */
  getLinks(id: number): Observable<any> {
    return this.apiService.get('/link_vs_staff_by_staffid/' + id);
  }

  /**
   * @method Get publication types for staff member by staff id according to types
   */
  getUniquePubTypes(id: number): Observable<any> {
    return this.apiService.get('/publications/bystaff/staffId/' + id);
  }

  /**
   * @method Get Specialities for staff member by staff id according to Speciality types
   */
  getSpecialities(id: number): Observable<any> {
    return this.apiService.get('/staff_specialty/types/member/' + id);
  }

  /**
   * @method Get Staff by Department ID with Category
   */
  getStaffByDeptByCat(id: number): Observable<any> {
    return this.apiService.get('/staff/bydept/bycat/' + id);
  }

  /**
   * @method Get All Staff members
   */
  getAllStaffMembers(id: number): Observable<any> {
    return this.apiService.get('/staff/bydept/bycat/' + id);
  }

  /**
   * @method Get All Staff members list
   */
  getStaffMemberList(): Observable<any> {
    return this.apiService.get('/staff/activestafflist');
  }

  /**
   * @method Get publication count
   */
  getPubCount(): Observable<any> {
    return this.apiService.get('/publications/count/facultyid/' + this.faculty_id);
  }

  /**
   * @method Get publication by publication area
   */
  getPubByArea(id: number): Observable<any> {
    return this.apiService.get('/publications/byarea/' + id);
  }

  /**
   * @method Get all publication areas
   */
  getPublicationAreas(): Observable<any> {
    return this.apiService.get('/publicationarea/viewall/facultyid/' + this.faculty_id);
  }

  /**
   * @method Get Links by Department id
   */
  getLinksByDept(id: number): Observable<any> {
    return this.apiService.get('/deptlinks_by_dept/' + id);
  }

  getWeather(): Observable<any> {
    return this.apiService.get('/geodata/today');
  }

}
